// src/pages/product/ProductDetail.tsx
//
// ProductDetail
//
// Single-page editor for an existing product.
// - Loads product data and hydrates a local "upsert" form state.
// - Keeps the shared PageHeader (title/breadcrumb) managed at the route-level,
//   and injects only contextual "leading" content (product name/code).
// - Uses global Confirm + System Alert providers for user prompts and feedback.
// - Places destructive action (Delete) in a dedicated Danger Zone section.

import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AttachMoneyOutlined,
  CheckCircleOutline,
  Close as CloseIcon,
  ContentCopy,
  DeleteOutline,
  Inventory2Outlined,
  PauseCircleOutline,
  ReportProblemOutlined,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';

import { ProductStatus, RequiredDataType } from '@models/product';
import {
  editProduct,
  ProductSaleType,
  ProductType,
  ProductUpsertRequest,
} from '@apis/product';
import { getStock, StockSupplierTaskType } from '@apis/stock';

import { useProductDetail } from '@queries/products/useProductDetail';
import { useAllStockList } from '@queries/stocks/useAllStockList';
import { useCountryList } from '@queries/countries/useCountryList';

import useAsyncEffect from '@hooks/useAsyncEffect';
import { isNullishOrValidJson } from '@utils/common';

import { usePageHeader } from '@components/common/pageHeader.context';
import { useConfirm } from '@components/common/confirm/confirm.context';
import { useSystemAlert } from '@components/common/systemAlert/systemAlert.context';

import DangerZoneCard from '@components/common/DangerZoneCard';
import ProductBasicInfoSection, {
  ProductPriceField,
} from '@components/product/ProductBasicInfoSection';
import { ProductSaleTypeDisplay } from '@components/product/ProductSaleTypeDisplay';
import ProductSpecificationSection from '@components/product/ProductSpecificationSection';
import ProductSettingsSection from '@components/product/ProductSettingsSection';
import {
  ProductTypeDisplay,
} from '@components/product/PartnerProductSpecDisplays';
import {
  PRODUCT_STATUS_LABELS,
  getProductStatusMeta,
} from '@utils/productLabels';
import type { HeaderAction } from '@components/common/pageHeader.types';
import { PRODUCT_DETAIL_PAGE_MAX_WIDTH } from '@constants/layout';

type Country = {
  countryId: number;
  englishName: string;
};

/**
 * Local form shape:
 * - Uses a flattened Country[] for coverage editing convenience.
 * - Converted back to API shape (ProductUpsertRequest) on submit by the API layer.
 */
type ProductUpsertForm = Omit<ProductUpsertRequest, 'productCoverages'> & {
  productCoverages: Country[];
};

type StockOption = { value: number; label: string };

const SUMMARY_ICON_SX = {
  fontSize: 17,
  flex: '0 0 auto',
} as const;

const formatUsdAmount = (value: unknown) => {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return '0.00';
  return n.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatApiParameterForEdit = (value: unknown) => {
  if (!value) return undefined;
  return JSON.stringify(value, null, 2);
};

const normalizeProductFormForDirty = (value: ProductUpsertForm) => ({
  ...value,
  apiParameter: value.apiParameter || undefined,
  stockId: value.stockId ?? null,
  productCoverages: [...value.productCoverages]
    .map((coverage) => ({
      countryId: coverage.countryId,
      englishName: coverage.englishName,
    }))
    .sort((a, b) => a.countryId - b.countryId),
  requiredData: {
    ...value.requiredData,
    requiredFields: [...(value.requiredData?.requiredFields ?? [])].sort(),
  },
  specification: {
    ...value.specification,
    dataCapacity: {
      ...value.specification.dataCapacity,
    },
  },
});

const isProductFormDirty = (
  current: ProductUpsertForm,
  initial: ProductUpsertForm | null,
) => {
  if (!initial) return false;

  return (
    JSON.stringify(normalizeProductFormForDirty(current)) !==
    JSON.stringify(normalizeProductFormForDirty(initial))
  );
};

const getProductStatusDisplay = (status?: ProductStatus | string | null) => {
  const meta = getProductStatusMeta(status);

  switch (status) {
    case ProductStatus.OUT_OF_STOCK:
      return {
        label: meta.label,
        color: 'warning.main',
        icon: <ReportProblemOutlined sx={SUMMARY_ICON_SX} />,
      };
    case ProductStatus.DISCONTINUED:
      return {
        label: meta.label,
        color: 'text.disabled',
        icon: <PauseCircleOutline sx={SUMMARY_ICON_SX} />,
      };
    case ProductStatus.ACTIVE:
    default:
      return {
        label: meta.label,
        color: 'success.main',
        icon: <CheckCircleOutline sx={SUMMARY_ICON_SX} />,
      };
  }
};

const SummaryValue = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => (
  <Stack spacing={0.5} sx={{ minWidth: 0 }}>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ fontWeight: 800, textTransform: 'uppercase' }}
    >
      {label}
    </Typography>
    <Box sx={{ minWidth: 0 }}>{children}</Box>
  </Stack>
);

const ProductStatusDisplay = ({
  status,
}: {
  status?: ProductStatus | string | null;
}) => {
  const meta = getProductStatusDisplay(status);

  return (
    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: meta.color }}>
      {meta.icon}
      <Typography
        component="span"
        variant="body2"
        sx={{ color: 'inherit', fontWeight: 800, lineHeight: 1.25 }}
      >
        {meta.label}
      </Typography>
    </Stack>
  );
};

const ProductSummaryCard = ({
  productCode,
  productStatus,
  productType,
  productSaleType,
  stockCode,
  price,
  onCopyProductCode,
}: {
  productCode: string;
  productStatus: ProductStatus;
  productType: ProductType;
  productSaleType: ProductSaleType;
  stockCode: string;
  price: number;
  onCopyProductCode: () => void;
}) => (
  <Card
    elevation={0}
    sx={(theme) => ({
      borderRadius: 2,
      border: `1px solid ${theme.palette.divider}`,
      mb: 3,
    })}
  >
    <CardContent>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, minmax(0, 1fr))',
            lg: 'repeat(3, minmax(0, 1fr))',
            xl: 'repeat(6, minmax(0, 1fr))',
          },
          columnGap: 2.5,
          rowGap: 2,
        }}
      >
        <SummaryValue label="Product Code">
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 800,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={productCode}
            >
              {productCode || '-'}
            </Typography>

            {productCode ? (
              <Tooltip title="Copy product code" arrow>
                <IconButton
                  size="small"
                  aria-label="Copy product code"
                  onClick={onCopyProductCode}
                  sx={{
                    width: 24,
                    height: 24,
                    color: 'text.secondary',
                    flexShrink: 0,
                    '&:hover': { color: 'primary.main' },
                  }}
                >
                  <ContentCopy sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
            ) : null}
          </Stack>
        </SummaryValue>

        <SummaryValue label="Stock Code">
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
            <Inventory2Outlined sx={{ ...SUMMARY_ICON_SX, color: 'text.secondary' }} />
            <Typography
              variant="body2"
              sx={{
                fontWeight: 800,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={stockCode}
            >
              {stockCode || '-'}
            </Typography>
          </Stack>
        </SummaryValue>

        <SummaryValue label="Status">
          <ProductStatusDisplay status={productStatus} />
        </SummaryValue>

        <SummaryValue label="Product Type">
          <ProductTypeDisplay productType={productType} />
        </SummaryValue>

        <SummaryValue label="Order Type">
          <ProductSaleTypeDisplay saleType={productSaleType} />
        </SummaryValue>

        <SummaryValue label="Price">
          <Stack direction="row" spacing={0.75} alignItems="center">
            <AttachMoneyOutlined sx={{ ...SUMMARY_ICON_SX, color: 'text.secondary' }} />
            <Typography variant="body2" sx={{ fontWeight: 800 }}>
              {formatUsdAmount(price)}
            </Typography>
          </Stack>
        </SummaryValue>
      </Box>
    </CardContent>
  </Card>
);

const AvailabilityPricingCard = ({
  productStatus,
  price,
  onChangeStatus,
  onChangePrice,
}: {
  productStatus: ProductStatus;
  price: number;
  onChangeStatus: (value: ProductStatus) => void;
  onChangePrice: (value: number) => void;
}) => (
  <Card
    elevation={0}
    sx={(theme) => ({
      borderRadius: 2,
      border: `1px solid ${theme.palette.divider}`,
      borderLeft: `4px solid ${theme.palette.primary.main}`,
      bgcolor: 'rgba(25, 118, 210, 0.025)',
    })}
  >
    <CardContent>
      <Stack spacing={2.25}>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Availability &amp; Pricing
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Sale availability and price.
          </Typography>
        </Box>

        <Stack spacing={1}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontWeight: 800, textTransform: 'uppercase' }}
          >
            Product Status
          </Typography>

          <Box
            role="group"
            aria-label="Product Status"
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
              gap: 0.75,
            }}
          >
            {Object.values(ProductStatus).map((status) => (
              <Button
                key={status}
                variant={productStatus === status ? 'contained' : 'outlined'}
                color={productStatus === status ? 'primary' : 'inherit'}
                onClick={() => onChangeStatus(status)}
                sx={{
                  minWidth: 0,
                  px: 0.75,
                  whiteSpace: 'normal',
                  lineHeight: 1.2,
                  textTransform: 'none',
                  fontWeight: 800,
                  borderColor: productStatus === status ? undefined : 'divider',
                }}
              >
                {PRODUCT_STATUS_LABELS[status]}
              </Button>
            ))}
          </Box>

          <Typography variant="caption" color="text.secondary">
            Controls product visibility in sales flows.
          </Typography>
        </Stack>

        <Box
          sx={{
            pt: 2,
            mt: 0.5,
            borderTop: '1px solid',
            borderColor: 'rgba(0, 0, 0, 0.06)',
          }}
        >
          <ProductPriceField price={price} onChangePrice={onChangePrice} fullWidth />
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

const ProductDetail = () => {
  const { productId } = useParams();
  const productIdNum = Number(productId);

  const navigate = useNavigate();
  const confirm = useConfirm();
  const systemAlert = useSystemAlert();
  const { setPageHeaderSlots } = usePageHeader();

  // ---------------------------------------------------------------------------
  // Local form state
  // ---------------------------------------------------------------------------

  const [form, setForm] = useState<ProductUpsertForm>({
    productCode: '',
    productSaleType: ProductSaleType.REAL_TIME,
    productType: ProductType.ESIM,
    productName: '',
    description: '',
    price: 0,
    specification: {
      dayCount: 0,
      isUnlimitedData: true,
      isVoiceCallSupported: false,
      dataCapacity: {
        quantity: 0,
        unit: 'GB',
        refreshPeriodUnit: 'NEVER',
        refreshPeriodValue: 0,
      },
      isFup: false,
    },
    productCoverages: [],
    requiredData: { requiredFields: [] },
    productStatus: ProductStatus.ACTIVE,
    apiParameter: undefined,
    stockId: 0,
  });
  const [initialForm, setInitialForm] = useState<ProductUpsertForm | null>(null);

  // ---------------------------------------------------------------------------
  // Data sources
  // ---------------------------------------------------------------------------

  const { data: productDetailForm } = useProductDetail(productIdNum);
  const { data: stocksData } = useAllStockList();
  const { data: countriesData } = useCountryList();

  const [stockOptionList, setStockOptionList] = useState<StockOption[]>([]);
  const [isApiInputNeeded, setIsApiInputNeeded] = useState(false);

  const allCountries: Country[] = useMemo(() => {
    return (countriesData?.data ?? []).map((c) => ({
      countryId: c.countryId,
      englishName: c.englishName,
    }));
  }, [countriesData]);

  // ---------------------------------------------------------------------------
  // Form update helpers (immutable updates)
  // ---------------------------------------------------------------------------

  /**
   * Updates a top-level field in the form state.
   * Use dedicated helpers for nested objects to keep updates explicit.
   */
  const updateField = useCallback(
    (
      name: keyof ProductUpsertRequest,
      value: string | number | boolean | null | undefined,
    ) => {
      setForm((prev) => ({ ...prev, [name]: value as any }));
    },
    [],
  );

  /** Toggles a RequiredDataType within requiredData.requiredFields. */
  const toggleRequiredField = useCallback(
    (field: RequiredDataType, checked: boolean) => {
      setForm((prev) => ({
        ...prev,
        requiredData: {
          ...prev.requiredData,
          requiredFields: checked
            ? [...prev.requiredData.requiredFields, field]
            : prev.requiredData.requiredFields.filter((r) => r !== field),
        },
      }));
    },
    [],
  );

  /** Updates a root field inside the `specification` object. */
  const updateSpecification = useCallback(
    (
      name: keyof ProductUpsertRequest['specification'],
      value: string | boolean | number | null,
    ) => {
      setForm((prev) => ({
        ...prev,
        specification: { ...prev.specification, [name]: value as any },
      }));
    },
    [],
  );

  /** Updates a nested field inside `specification.dataCapacity`. */
  const updateDataCapacity = useCallback(
    (
      name: keyof ProductUpsertRequest['specification']['dataCapacity'],
      value: number | string,
    ) => {
      setForm((prev) => ({
        ...prev,
        specification: {
          ...prev.specification,
          dataCapacity: {
            ...prev.specification.dataCapacity,
            [name]: value as any,
          },
        },
      }));
    },
    [],
  );

  // ---------------------------------------------------------------------------
  // Coverage helpers
  // ---------------------------------------------------------------------------

  const addCoverage = useCallback((country: Country) => {
    if (!country?.countryId) return;

    setForm((prev) => {
      const exists = prev.productCoverages.some(
        (c) => c.countryId === country.countryId,
      );
      if (exists) return prev;

      return { ...prev, productCoverages: [...prev.productCoverages, country] };
    });
  }, []);

  const deleteCoverage = useCallback((countryId: number) => {
    setForm((prev) => ({
      ...prev,
      productCoverages: prev.productCoverages.filter((c) => c.countryId !== countryId),
    }));
  }, []);

  const isDirty = useMemo(
    () => isProductFormDirty(form, initialForm),
    [form, initialForm],
  );

  // ---------------------------------------------------------------------------
  // Page actions
  // ---------------------------------------------------------------------------

  const handleCancel = useCallback(async () => {
    const ok = await confirm({
      priority: 'info',
      title: 'Cancel changes?',
      description: 'Your unsaved changes will be lost.',
      confirmText: 'Leave',
      cancelText: 'Stay',
      autoFocusConfirm: false,
    });

    if (ok) navigate(-1);
  }, [confirm, navigate]);

  const handleSave = useCallback(async () => {
    if (!isDirty) return;

    try {
      // Keep client validation consistent with existing behavior.
      if (!isNullishOrValidJson(form.apiParameter)) {
        systemAlert.error('Invalid JSON format for apiParameter.');
        return;
      }

      await editProduct(productIdNum, {
        ...form,
        stockId: form.stockId,
      });

      systemAlert.success('Product edited successfully');
      setInitialForm(form);
    } catch {
      systemAlert.error('Failed to save');
    }
  }, [form, isDirty, productIdNum, systemAlert]);

  const handleCopyProductCode = useCallback(async () => {
    const text = form.productCode.trim();
    if (!text) return;

    try {
      await navigator.clipboard.writeText(text);
      systemAlert.success('Product code copied.', { durationMs: 1600 });
    } catch {
      systemAlert.error('Copy failed.');
    }
  }, [form.productCode, systemAlert]);

  const handleDelete = useCallback(async () => {
    const ok = await confirm({
      priority: 'danger',
      title: 'Delete Product',
      description: 'Permanently removes this product. This action cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      autoFocusConfirm: false,
    });

    if (!ok) return;

    try {
      await editProduct(productIdNum, {
        ...form,
        stockId: form.stockId,
        isDeleted: true,
      });

      systemAlert.success('Product deleted successfully');
      navigate('/products');
    } catch {
      systemAlert.error('Failed to delete');
    }
  }, [confirm, form, navigate, productIdNum, systemAlert]);

  // ---------------------------------------------------------------------------
  // Effects
  // ---------------------------------------------------------------------------

  /** Build Stock select options (sorted by stock code). */
  useAsyncEffect(async () => {
    if (!stocksData) return;

    const sorted: StockOption[] = [...stocksData.data]
      .map((s) => ({ label: s.stockCode, value: s.stockId }))
      .sort((a, b) => a.label.localeCompare(b.label));

    setStockOptionList(sorted);
  }, [stocksData]);

  /** Detect if the selected stock requires API parameter input. */
  useAsyncEffect(async () => {
    if (!form.stockId) {
      setIsApiInputNeeded(false);
      return;
    }

    const { data } = await getStock(form.stockId);
    const stock = data?.[0];
    if (!stock) return;

    setIsApiInputNeeded(stock.supplierTaskType === StockSupplierTaskType.API);
  }, [form.stockId]);

  /** Hydrate the local form from the fetched product detail. */
  useEffect(() => {
    const base = productDetailForm?.data?.[0];
    if (!base || !productIdNum) return;

    const coverages: Country[] =
      base.productCoverages?.map((c) => ({
        countryId: c.country.countryId,
        englishName: c.country.englishName,
      })) ?? [];

    const nextForm: ProductUpsertForm = {
      ...base,
      apiParameter: formatApiParameterForEdit(base.apiParameter),
      stockId: base.stock.stockId,
      productCoverages: coverages,
    };

    setForm(nextForm);
    setInitialForm(nextForm);
  }, [productDetailForm, productIdNum]);

  /**
   * Provide contextual "leading" content for the shared page header.
   * The header component itself owns the main title/breadcrumb layout.
   */
  const headerLeading = useMemo(() => {
    const title = form.productName || 'Product detail';

    return { title };
  }, [form.productName]);

  const headerActions = useMemo<HeaderAction[]>(
    () => [
      {
        key: 'cancel',
        label: 'Cancel',
        variant: 'outlined',
        color: 'error',
        startIcon: <CloseIcon />,
        onClick: handleCancel,
      },
      {
        key: 'save',
        label: 'Save changes',
        variant: 'contained',
        startIcon: <SaveIcon />,
        primary: true,
        disabled: !isDirty,
        onClick: handleSave,
      },
    ],
    [handleCancel, handleSave, isDirty],
  );

  useEffect(() => {
    setPageHeaderSlots({
      leading: headerLeading,
      actions: headerActions,
    });
  }, [headerActions, headerLeading, setPageHeaderSlots]);

  // ---------------------------------------------------------------------------
  // Derived values
  // ---------------------------------------------------------------------------

  const requiredFields = form.requiredData?.requiredFields ?? [];
  const selectedStockCode = useMemo(() => {
    return stockOptionList.find((stock) => stock.value === form.stockId)?.label ?? '-';
  }, [form.stockId, stockOptionList]);

  // ---------------------------------------------------------------------------

  return (
    <Box sx={{ py: 2, maxWidth: PRODUCT_DETAIL_PAGE_MAX_WIDTH, width: '100%', mr: 'auto' }}>
      <ProductSummaryCard
        productCode={form.productCode}
        productStatus={form.productStatus}
        productType={form.productType}
        productSaleType={form.productSaleType}
        stockCode={selectedStockCode}
        price={form.price}
        onCopyProductCode={handleCopyProductCode}
      />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 360px', xl: 'minmax(0, 1fr) 380px' },
          gap: 3,
          alignItems: 'start',
        }}
      >
        <Stack spacing={3} sx={{ minWidth: 0 }}>
          <ProductBasicInfoSection
            productCode={form.productCode}
            showProductCode={false}
            productName={form.productName}
            description={form.description}
            price={form.price}
            showPrice={false}
            onChangeName={(v) => updateField('productName', v)}
            onChangeDescription={(v) => updateField('description', v)}
            onChangePrice={(v) => updateField('price', v)}
          />

          <ProductSpecificationSection
            specification={form.specification}
            coverages={form.productCoverages}
            allCountries={allCountries}
            onChangeSpecification={updateSpecification}
            onChangeSpecificationData={updateDataCapacity}
            onAddCoverage={addCoverage}
            onDeleteCoverage={deleteCoverage}
          />
        </Stack>

        <Stack spacing={3} sx={{ minWidth: 0 }}>
          <AvailabilityPricingCard
            productStatus={form.productStatus}
            price={form.price}
            onChangeStatus={(value) => updateField('productStatus', value)}
            onChangePrice={(value) => updateField('price', value)}
          />

          <ProductSettingsSection
            compact
            showProductStatus={false}
            productStatus={form.productStatus}
            productSaleType={form.productSaleType}
            productType={form.productType}
            requiredFields={requiredFields}
            stockId={form.stockId || null}
            stockOptions={stockOptionList}
            apiParameter={form.apiParameter ?? null}
            isApiInputNeeded={isApiInputNeeded}
            onChangeStatus={(value) => updateField('productStatus', value)}
            onChangeSaleType={(value) => updateField('productSaleType', value)}
            onChangeProductType={(value) => updateField('productType', value)}
            onToggleRequiredField={toggleRequiredField}
            onChangeStockId={(value) => updateField('stockId', value === null ? null : value)}
            onChangeApiParameter={(value) => updateField('apiParameter', value ?? undefined)}
          />

          <DangerZoneCard
            priority="danger"
            title="Delete Product"
            description="Permanently removes this product. This action cannot be undone."
            actions={
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteOutline />}
                onClick={handleDelete}
                sx={{ width: { xs: '100%', sm: 'auto', lg: '100%' }, whiteSpace: 'nowrap' }}
              >
                Delete Product
              </Button>
            }
          />
        </Stack>
      </Box>
    </Box>
  );
};

export default ProductDetail;
